import { ButtonDropdownProps } from '@cloudscape-design/components/button-dropdown';
import { TopNavigationProps } from '@cloudscape-design/components/top-navigation';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslate } from 'src/i18n/useTranslate';
import { PAGE } from 'src/pages/enums';
import {
    isUnreadForUser,
    RecentlyUpdatedEntry,
    useRecentlyUpdatedList,
    useUpdatesReadState,
} from 'src/hooks/useRecentlyUpdated';

/**
 * Recently_Updated_Bell — top-navigation menu-dropdown utility that
 * surfaces the newest 5 global updates and marks itself with an unread
 * dot while the user has not yet visited `/recently-updated` since the
 * most recent change.
 *
 * Shape per design.md § RecentlyUpdatedBell component. Cloudscape's
 * `TopNavigation.utilities` prop requires plain objects (not JSX), so
 * this file exports a hook that returns a
 * `TopNavigationProps.MenuDropdownUtility` which the Navigation.tsx file
 * spreads into the utilities array. A thin React wrapper is also
 * exported so component-level tests can mount the bell in isolation.
 *
 * Interaction model:
 *  - Click opens the Cloudscape dropdown with the 5 items + a trailing
 *    "Show more" item that navigates to `/${PAGE.RECENTLY_UPDATED}`.
 *  - Each entry links to the learning-artifact detail page and
 *    deep-links to the newest-changed topic via the hash the detail
 *    page already parses (`#/<lcmsXmlGUID>`).
 *  - Opening the dropdown does NOT clear the unread dot. Only the
 *    `/recently-updated` page visit clears the read state (req 4.6).
 *
 * Click-only interaction (no hover preview). See
 * `.kiro/specs/recently-updated-notifications/design.md §
 * "RecentlyUpdatedBell component"` for the rationale: Cloudscape's
 * `Popover.triggerType` enumerates `'text' | 'text-inline' | 'custom'`,
 * so `triggerType='hover'` does not exist, and `GlossaryTermHighlight.tsx`
 * documents the project convention against hand-rolled hover mirrors
 * (Cloudscape manages its own open state and a shadow copy silently
 * drifts). The click dropdown surfaces the same 5-item preview on both
 * desktop and mobile, so the convenience loss is minimal and the
 * maintenance surface stays inside Cloudscape chrome.
 */

export const MENU_ITEM_SHOW_MORE_ID = 'recently-updated-show-more';
export const MENU_ITEM_EMPTY_ID = 'recently-updated-empty';
export const MENU_ITEM_PREFIX = 'recently-updated-entry';
export const SHOW_MORE_HREF = `/${PAGE.RECENTLY_UPDATED}`;

/**
 * Returns true when the newest entry's `latestChangeAt` is strictly
 * newer than the user's `lastViewedAt`. When `lastViewedAt` is null the
 * dot is shown whenever the list has at least one entry (req 4.2).
 *
 * Delegates to the shared `isUnreadForUser` helper so the bell and the
 * Personal_Updates_Banner stay in agreement — both compare in the
 * epoch-ms domain, which (unlike ISO-string lexicographic ordering)
 * handles backend timestamps that omit milliseconds correctly.
 */
export const isUnread = (
    entries: RecentlyUpdatedEntry[] | undefined,
    lastViewedAt: number | null
): boolean => isUnreadForUser(entries, lastViewedAt);

/**
 * Builds the deep-link href for a Recently_Updated_Document_Entry. The
 * learning-artifact detail page is routed at `/:artifactId` and parses
 * the hash as `#/<lcmsXmlGUID>` (see learning-artifact-detail-page.tsx).
 */
export const buildEntryHref = (entry: RecentlyUpdatedEntry): string =>
    `/${entry.documentId}#/${entry.latestChangedTopicGuid}`;

interface BuildUtilityArgs {
    entries?: RecentlyUpdatedEntry[];
    lastViewedAt: number | null;
    emptyText: string;
    showMoreText: string;
    ariaLabel: string;
    onItemClick: (href: string) => void;
}

export const buildRecentlyUpdatedUtility = ({
    entries,
    lastViewedAt,
    emptyText,
    showMoreText,
    ariaLabel,
    onItemClick,
}: BuildUtilityArgs): TopNavigationProps.MenuDropdownUtility => {
    // `ButtonDropdownProps.Items` is a ReadonlyArray — build on a
    // mutable array of `ItemOrGroup` and assign to the readonly slot.
    const items: ButtonDropdownProps.ItemOrGroup[] = [];
    const idToHref = new Map<string, string>();

    if (!entries || entries.length === 0) {
        items.push({
            id: MENU_ITEM_EMPTY_ID,
            text: emptyText,
            disabled: true,
        });
    } else {
        entries.slice(0, 5).forEach((entry, idx) => {
            const id = `${MENU_ITEM_PREFIX}-${idx}`;
            const href = buildEntryHref(entry);
            idToHref.set(id, href);
            // `href` is set for right-click / middle-click accessibility
            // (open-in-new-tab works natively). Normal clicks are routed
            // through onItemFollow / onItemClick below so react-router
            // handles the SPA navigation instead of a full page reload.
            items.push({
                id,
                text: entry.documentTitle,
                href,
            });
        });
        const showMoreId = MENU_ITEM_SHOW_MORE_ID;
        idToHref.set(showMoreId, SHOW_MORE_HREF);
        items.push({
            id: showMoreId,
            text: showMoreText,
            href: SHOW_MORE_HREF,
        });
    }

    const route = (
        event: Parameters<
            NonNullable<TopNavigationProps.MenuDropdownUtility['onItemClick']>
        >[0]
    ) => {
        const href = idToHref.get(event.detail.id);
        if (!href) return;
        event.preventDefault();
        onItemClick(href);
    };

    return {
        type: 'menu-dropdown',
        iconName: 'notification',
        ariaLabel,
        badge: isUnread(entries, lastViewedAt),
        items,
        // onItemFollow handles plain clicks on items that carry an
        // href; onItemClick covers the (currently unused) non-href
        // branches. Attaching both is cheap and keeps the dropdown
        // SPA-friendly regardless of future item shape changes.
        onItemClick: route,
        onItemFollow: route,
    };
};

/**
 * Hook form used by Navigation.tsx. Fetches the global list (limit 5)
 * and the user's read-state, and returns a utility definition ready to
 * drop into the TopNavigation `utilities` array.
 */
export const useRecentlyUpdatedBellUtility =
    (): TopNavigationProps.MenuDropdownUtility => {
        const t = useTranslate();
        const navigate = useNavigate();
        const { data: entries } = useRecentlyUpdatedList('global', 5);
        const { lastViewedAt } = useUpdatesReadState();

        const emptyText = t('recently-updated-bell-empty', 'No recent updates');
        const showMoreText = t('recently-updated-bell-show-more', 'Show more');
        const ariaLabel = t(
            'recently-updated-bell-aria',
            'Recently updated documents'
        );

        const onItemClick = useCallback(
            (href: string) => {
                navigate(href);
            },
            [navigate]
        );

        return useMemo(
            () =>
                buildRecentlyUpdatedUtility({
                    entries,
                    lastViewedAt,
                    emptyText,
                    showMoreText,
                    ariaLabel,
                    onItemClick,
                }),
            [
                entries,
                lastViewedAt,
                emptyText,
                showMoreText,
                ariaLabel,
                onItemClick,
            ]
        );
    };

/**
 * Thin React wrapper mounted by unit tests. The real surface is the
 * utility slot in Navigation.tsx; this wrapper intentionally renders
 * only the utility's identifying bits onto the DOM so component-level
 * tests can assert against them without wiring up a full
 * `TopNavigation` harness (which Cloudscape mounts asynchronously).
 */
export const RecentlyUpdatedBell = () => {
    const utility = useRecentlyUpdatedBellUtility();
    const payload = JSON.stringify({
        badge: utility.badge,
        itemCount: Array.isArray(utility.items) ? utility.items.length : 0,
        iconName: utility.iconName,
    });
    return (
        <div
            data-testid="recently-updated-bell"
            data-utility={payload}
            data-badge={utility.badge ? 'on' : 'off'}
        >
            {Array.isArray(utility.items) &&
                utility.items.map((item, idx) => {
                    const id = 'id' in item ? item.id : `idx-${idx}`;
                    const text = 'text' in item ? item.text : '';
                    const href = 'href' in item ? item.href : '';
                    const disabled =
                        'disabled' in item ? Boolean(item.disabled) : false;
                    return (
                        <a
                            key={id}
                            data-testid={`recently-updated-bell-item-${id}`}
                            href={href}
                            aria-disabled={disabled ? 'true' : undefined}
                        >
                            {text}
                        </a>
                    );
                })}
        </div>
    );
};

export default RecentlyUpdatedBell;
